import { useQueries, useQuery, useMutation } from "@tanstack/react-query";
import { queryClient } from "@src/utils/queryClient";
import * as adminService from "@src/services/adminService";
import * as sharedService from "@src/services/sharedServices";
import { adminEndpoints } from "@src/services/endpoints";
import SignolUtils from "@src/utils/signolUtils";
import AppUtils from "@src/utils/appUtils";
import { DEFAULT_LANGUAGE } from "@src/constants/constants";

const MILLISECONDS_IN_TEN_MINUTES = 10 * 60 * 1000;

// AdminContainer inner components will rely on the QueryClient CACHE (staled data) as primary source of state-sync
const AdminHooks = {
  useGetBehiveOutputs: (role, customer) =>
    useQuery({
      queryKey: ["BEHIVE_OUTPUTS", customer],
      queryFn: () => sharedService.getBehiveOutputs(customer),
      enabled: SignolUtils.isAdmin(role),
      staleTime: MILLISECONDS_IN_TEN_MINUTES,
      select: (data) => {
        if (data.error) {
          console.error("api error:", data);
          throw Error(data);
        }
        return data;
      },
    }),
  useGetDynamicContent: (role, industry) =>
    useQuery({
      queryKey: ["GLOBAL_DYNAMIC_CONTENT", role, industry],
      queryFn: () =>
        sharedService.getDynamicContent(DEFAULT_LANGUAGE, industry),
      enabled: SignolUtils.isAdmin(role),
      staleTime: MILLISECONDS_IN_TEN_MINUTES,
      select: (data) => {
        if (data.error) {
          console.error("api error:", data);
          throw Error(data);
        }
        return {
          [industry]: data.data.dynamic_content,
        };
      },
    }),

  useGetDynamicContentSettings: (role) =>
    useQuery({
      queryKey: ["DYNAMIC_CONTENT_SETTINGS", role],
      queryFn: () => adminService.getDynamicContentSettings(),
      enabled: SignolUtils.isAdmin(role),
      staleTime: MILLISECONDS_IN_TEN_MINUTES,
      select: (data) => {
        if (data.error) {
          console.error("api error:", data);
          throw Error(data);
        }
        return {
          industries: data.industries,
          languages: data.languages,
        };
      },
    }),

  useGetAllDynamicContent: (role) => {
    const {
      data: settingsData,
      isPending: settingsPending,
      error: settingsError,
    } = AdminHooks.useGetDynamicContentSettings(role);

    const industries = settingsData?.industries || [];
    const industriesList = settingsData?.industries.map((i) => i.name) || [];
    const languages = settingsData?.languages || [];
    const language = settingsData?.languages?.[0] || DEFAULT_LANGUAGE;
    const allIndustries = ["global", ...industriesList];

    const queries = useQueries({
      queries: allIndustries.map((industry) => ({
        queryKey: ["GLOBAL_DYNAMIC_CONTENT", role, industry],
        queryFn: () => sharedService.getDynamicContent(language, industry),
        enabled: SignolUtils.isAdmin(role) && !!settingsData,
        staleTime: MILLISECONDS_IN_TEN_MINUTES,
        select: (response) => {
          if (response.error) {
            console.error("api error:", response);
            throw Error(response);
          }

          const rawContent = response.data.dynamic_content;
          const inheritedMap = response.data.global_inherited;
          const industryName = response.data.industry;

          // Filter out inherited keys for industry responses only
          const filteredContent =
            industryName === "global"
              ? rawContent
              : Object.fromEntries(
                  Object.entries(rawContent).filter(
                    ([key]) => inheritedMap?.[key] === false,
                  ),
                );

          return {
            [industryName]: filteredContent,
          };
        },
      })),
    });

    const isPending = queries.some((q) => q.isPending);
    const isSettled = queries.every((q) => q.isSuccess || q.isError);
    const error = queries.find((q) => q.error)?.error;
    const allContent = queries.reduce((acc, q) => {
      if (q.data) Object.assign(acc, q.data);
      return acc;
    }, {});

    return {
      data: allContent,
      industries,
      allIndustries,
      languages,
      isPending,
      isSettled,
      error,
      settingsPending,
      settingsError,
    };
  },

  useGetBehaviours: (role, customerCode) =>
    useQuery({
      queryKey: ["ADMIN_BEHAVIOURS", role, customerCode],
      queryFn: () => adminService.getBehaviours(customerCode),
      enabled: SignolUtils.isAdmin(role),
      staleTime: MILLISECONDS_IN_TEN_MINUTES,
      select: (data) => {
        if (data.error) {
          console.error("api error:", data);
          throw Error(data);
        }
        return data;
      },
    }),

  useGetOperators: (role, customerCode) =>
    useQuery({
      queryKey: ["ADMIN_OPERATORS", role, customerCode],
      queryFn: () => adminService.getOperators(customerCode),
      enabled: SignolUtils.isAdmin(role),
      staleTime: MILLISECONDS_IN_TEN_MINUTES,
      select: (data) => {
        if (data.error) {
          console.error("api error:", data);
          throw Error(data);
        }
        return data.operators;
      },
    }),
  useGetOperator: (role, operatorId) =>
    useQuery({
      queryKey: ["ADMIN_OPERATOR", role, operatorId],
      queryFn: () => adminService.getOperator(operatorId),
      enabled: SignolUtils.isAdmin(role),
      staleTime: MILLISECONDS_IN_TEN_MINUTES,
      select: (data) => {
        if (data.error) {
          console.error("api error:", data);
          throw Error(data);
        }
        return data;
      },
    }),
  useGetOperatorNotifications: (role, customerCode, operatorCode) =>
    useQuery({
      queryKey: [
        "ADMIN_OPERATOR_NOTIFICATIONS",
        role,
        customerCode,
        operatorCode,
      ],
      queryFn: () =>
        adminService.getOperatorNotifications(customerCode, operatorCode),
      enabled: SignolUtils.isAdmin(role) && !!customerCode && !!operatorCode,
      staleTime: MILLISECONDS_IN_TEN_MINUTES,
      select: (data) => {
        if (data.error) {
          console.error("api error:", data);
          throw Error(data);
        }
        return data.notifications;
      },
    }),
  /** For a customer + template, the most recent sentAt per operatorCode that has ever received it.
   * Used to drive the "hasn't received [template] in the last [N] days" SEND TO filter. */
  useGetLastSentDates: (role, customerCode, template) =>
    useQuery({
      queryKey: ["ADMIN_NOTIFICATIONS_LAST_SENT", role, customerCode, template],
      queryFn: () => adminEndpoints.getLastSentDates(customerCode, template),
      enabled: SignolUtils.isAdmin(role) && !!customerCode && !!template,
      staleTime: MILLISECONDS_IN_TEN_MINUTES,
      select: (data) => {
        if (data.error) {
          console.error("api error:", data);
          throw Error(data);
        }
        return data.lastSentDates;
      },
    }),
  useGetSingleNotification: (role, notificationId) =>
    useQuery({
      queryKey: ["ADMIN_NOTIFICATION", role, notificationId],
      queryFn: () => adminService.getNotification(notificationId),
      enabled: SignolUtils.isAdmin(role) && !!notificationId,
      staleTime: MILLISECONDS_IN_TEN_MINUTES,
      select: (data) => {
        if (data.error) {
          console.error("api error:", data);
          throw Error(data);
        }
        return data.notification;
      },
    }),
  useGetNotificationTemplateConfigs: (role) =>
    useQuery({
      queryKey: ["ADMIN_NOTIFICATION_TEMPLATE_CONFIGS", role],
      queryFn: () => adminService.getNotificationTemplateConfigs(),
      enabled: SignolUtils.isAdmin(role),
      staleTime: MILLISECONDS_IN_TEN_MINUTES,
      select: (data) => {
        if (data.error) {
          console.error("api error:", data);
          throw Error(data);
        }
        return data.templates;
      },
    }),
  useGetOperatorSignupLink: (
    role,
    customerCode,
    operatorCode,
    alreadySignedUp = false,
  ) =>
    useQuery({
      queryKey: [
        "ADMIN_OPERATOR_SIGNUP_LINK",
        role,
        customerCode,
        operatorCode,
      ],
      queryFn: () =>
        adminService.getOperatorSignupLink(customerCode, operatorCode),
      enabled:
        SignolUtils.isAdmin(role) &&
        !!customerCode &&
        !!operatorCode &&
        !alreadySignedUp,
      staleTime: MILLISECONDS_IN_TEN_MINUTES,
      select: (data) => {
        if (data.error) {
          console.error("api error:", data);
          throw Error(data);
        }
        return data.signupLink;
      },
    }),

  useGetCharities: (role, customerCode) =>
    useQuery({
      queryKey: ["ADMIN_CHARITIES", role, customerCode],
      queryFn: () => adminService.getCharities(customerCode),
      enabled: SignolUtils.isAdmin(role),
      staleTime: MILLISECONDS_IN_TEN_MINUTES,
      select: (data) => {
        if (data.error) {
          console.error("api error:", data);
          throw Error(data);
        }
        return data;
      },
    }),

  useGetTeams: (role, customerId) =>
    useQuery({
      queryKey: ["ADMIN_TEAMS", role, customerId],
      queryFn: () => adminService.getTeams(customerId),
      enabled: SignolUtils.isAdmin(role),
      staleTime: MILLISECONDS_IN_TEN_MINUTES,
      select: (data) => {
        if (data.error) {
          console.error("api error:", data);
          throw Error(data);
        }
        return data;
      },
    }),

  // mutations

  useSaveDynamicContent: (callback) =>
    useMutation({
      mutationFn: (data) => adminEndpoints.saveDynamicContent(data),
      onSuccess: (data, variables) => {
        callback && callback();
        const { industry } = variables;
        queryClient.invalidateQueries({
          queryKey: ["GLOBAL_DYNAMIC_CONTENT", "ADMINISTRATOR", industry],
        });
        console.log("DC updated successfully", data);
      },
      onError: (error) => {
        console.error("Error updating DC", error);
      },
    }),

  useDeleteDynamicContent: (callback) =>
    useMutation({
      mutationFn: (data) => adminEndpoints.deleteDynamicContent(data),
      onSuccess: (data, variables) => {
        callback && callback();
        const { industry } = variables;
        queryClient.invalidateQueries({
          queryKey: ["GLOBAL_DYNAMIC_CONTENT", "ADMINISTRATOR", industry],
        });
        console.log("DC deleted successfully", data);
      },
      onError: (error) => {
        console.error("Error deleting DC", error);
      },
    }),

  useCreateCustomer: (callback) =>
    useMutation({
      mutationFn: (data) => adminEndpoints.createCustomer(data),
      onSuccess: async (data, variables) => {
        await queryClient.refetchQueries({
          queryKey: ["ADMIN_CUSTOMERS"],
        });
        console.log("Customers created successfully", data);
        if (variables.customerCode) {
          // variables should be the original input passed to mutateAsync (the customer payload)
          await adminEndpoints.createCustomerIdentityPool(variables);
        }
        callback && callback(data);
      },
      onError: (error) => {
        console.error("Error creating customer", error);
      },
    }),

  useUpdateCustomer: (callback) =>
    useMutation({
      mutationFn: (data) => adminEndpoints.updateCustomer(data),
      onSuccess: async (data) => {
        await queryClient.refetchQueries({
          queryKey: ["ADMIN_CUSTOMERS"],
        });
        callback && callback(data);
        console.log("Customers updated successfully", data);
      },
      onError: (error) => {
        console.error("Error updating customer", error);
      },
    }),

  useUpdateBehaviour: (callback) =>
    useMutation({
      mutationFn: (data) => adminEndpoints.updateBehaviour(data),
      onSuccess: (data) => {
        queryClient.invalidateQueries({
          queryKey: ["ADMIN_BEHAVIOURS"],
        });
        if (data.error) throw new Error(data.message);
        callback && callback(data);
        console.log("Behaviour updated successfully", data);
      },
      onError: (error) => {
        console.error("Error updating behaviour", error);
      },
    }),

  useCreateBehaviour: (callback) =>
    useMutation({
      mutationFn: (data) => adminEndpoints.createBehaviour(data),
      onSuccess: (data) => {
        queryClient.invalidateQueries({
          queryKey: ["ADMIN_BEHAVIOURS"],
        });
        if (data.error) throw new Error(data.message);
        callback && callback(data);
        console.log("Behaviour created successfully", data);
      },
      onError: (error) => {
        console.error("Error creating behaviour", error);
      },
    }),

  useDeleteBehaviour: (callback) =>
    useMutation({
      mutationFn: (data) => adminEndpoints.deleteBehaviour(data),
      onSuccess: (data) => {
        queryClient.invalidateQueries({
          queryKey: ["ADMIN_BEHAVIOURS"],
        });
        if (data.error) throw new Error(data.message);
        callback && callback(data);
        console.log("Behaviour deleted successfully", data);
      },
      onError: (error) => {
        console.error("Error deleting behaviour", error);
      },
    }),

  useUpdateCharity: (callback) =>
    useMutation({
      mutationFn: (data) => adminEndpoints.updateCharity(data),
      onSuccess: (data) => {
        queryClient.invalidateQueries({
          queryKey: ["ADMIN_CHARITIES"],
        });
        if (data.error) throw new Error(data.message);
        callback && callback(data);
        console.log("Charity updated successfully", data);
      },
      onError: (error) => {
        console.error("Error updating charity", error);
      },
    }),

  useCreateCharity: (callback) =>
    useMutation({
      mutationFn: (data) => adminEndpoints.createCharity(data),
      onSuccess: (data) => {
        queryClient.invalidateQueries({
          queryKey: ["ADMIN_CHARITIES"],
        });
        if (data.error) throw new Error(data.message);
        callback && callback(data);
        console.log("Charity created successfully", data);
      },
      onError: (error) => {
        console.error("Error creating charity", error);
      },
    }),

  useDeleteCharity: (callback) =>
    useMutation({
      mutationFn: (data) => adminEndpoints.deleteCharity(data),
      onSuccess: (data) => {
        queryClient.invalidateQueries({
          queryKey: ["ADMIN_CHARITIES"],
        });
        if (data.error) throw new Error(data.message);
        callback && callback(data);
        console.log("Charity deleted successfully", data);
      },
      onError: (error) => {
        console.error("Error deleting charity", error);
      },
    }),

  useCreateTeam: (callback) =>
    useMutation({
      mutationFn: (data) =>
        adminEndpoints.createTeam(data.customerId, data.team),
      onSuccess: (data) => {
        queryClient.invalidateQueries({
          queryKey: ["ADMIN_TEAMS"],
        });
        if (data.error) throw new Error(data.message);
        callback && callback(data);
        console.log("Team created successfully", data);
      },
      onError: (error) => {
        console.error("Error creating Team", error);
      },
    }),

  useUpdateTeam: (callback) =>
    useMutation({
      mutationFn: (data) =>
        adminEndpoints.updateTeam(data.customerId, data.team),
      onSuccess: (data) => {
        queryClient.invalidateQueries({
          queryKey: ["ADMIN_TEAMS"],
        });
        if (data.error) throw new Error(data.message);
        callback && callback(data);
        console.log("Team updated successfully", data);
      },
      onError: (error) => {
        console.error("Error updating Team", error);
      },
    }),

  useDeleteTeam: (callback) =>
    useMutation({
      mutationFn: ({ customerId, teamId }) =>
        adminEndpoints.deleteTeam(customerId, teamId),
      onSuccess: (data) => {
        queryClient.invalidateQueries({
          queryKey: ["ADMIN_TEAMS"],
        });
        if (data.error) throw new Error(data.message);
        callback && callback(data);
        console.log("Team deleted successfully", data);
      },
      onError: (error) => {
        console.error("Error deleting Team", error);
      },
    }),

  useUpdateTarget: (callback) =>
    useMutation({
      mutationFn: ({ customerId, teamId, data }) =>
        adminEndpoints.updateTarget(customerId, teamId, data),
      onSuccess: (data) => {
        queryClient.invalidateQueries({
          queryKey: ["ADMIN_TEAMS"],
        });
        if (data.error) throw new Error(data.message);
        callback && callback(data);
        console.log("Target updated successfully", data);
      },
      onError: (error) => {
        console.error("Error updating Target", error);
      },
    }),

  useCreateTarget: (callback) =>
    useMutation({
      mutationFn: ({ customerId, teamId, data }) =>
        adminEndpoints.createTarget(customerId, teamId, data),
      onSuccess: (data) => {
        queryClient.invalidateQueries({
          queryKey: ["ADMIN_TEAMS"],
        });
        if (data.error) throw new Error(data.message);
        callback && callback(data);
        console.log("Target created successfully", data);
      },
      onError: (error) => {
        console.error("Error creating Target", error);
      },
    }),

  useDeleteTarget: (callback) =>
    useMutation({
      mutationFn: ({ customerId, teamId, targetId }) =>
        adminEndpoints.deleteTarget(customerId, teamId, targetId),
      onSuccess: (data) => {
        queryClient.invalidateQueries({
          queryKey: ["ADMIN_TEAMS"],
        });
        if (data.error) throw new Error(data.message);
        callback && callback(data);
        console.log("Target deleted successfully", data);
      },
      onError: (error) => {
        console.error("Error deleting Target", error);
      },
    }),

  useGetDonationsTool: (role, customerCode) =>
    useQuery({
      queryKey: ["ADMIN_DONATIONS_TOOL", role, customerCode],
      queryFn: () => adminService.getDonationsTool(customerCode),
      enabled: SignolUtils.isAdmin(role) && !!customerCode,
      staleTime: MILLISECONDS_IN_TEN_MINUTES,
      select: (data) => {
        if (data.error) {
          console.error("api error:", data);
          throw Error(data);
        }
        return data;
      },
    }),

  useGetBehaviourTypes: (role) =>
    useQuery({
      queryKey: ["ADMIN_BEHAVIOUR_TYPES", role],
      queryFn: () => adminService.getBehaviourTypes(),
      enabled: SignolUtils.isAdmin(role),
      staleTime: MILLISECONDS_IN_TEN_MINUTES,
      select: (data) => {
        if (data.error) {
          console.error("api error:", data);
          throw Error(data);
        }
        return data;
      },
    }),

  useGetBehaviourGroups: (role) =>
    useQuery({
      queryKey: ["ADMIN_BEHAVIOUR_GROUPS", role],
      queryFn: () => adminService.getBehaviourGroups(),
      enabled: SignolUtils.isAdmin(role),
      staleTime: MILLISECONDS_IN_TEN_MINUTES,
      select: (data) => {
        if (data.error) {
          console.error("api error:", data);
          throw Error(data);
        }
        return data;
      },
    }),

  useGetCustomerBehaviourGroups: (role, customerId) =>
    useQuery({
      queryKey: ["ADMIN_CUSTOMER_BEHAVIOUR_GROUPS", role, customerId],
      queryFn: () => adminService.getCustomerBehaviourGroups(customerId),
      enabled: SignolUtils.isAdmin(role) && !!customerId,
      staleTime: MILLISECONDS_IN_TEN_MINUTES,
      select: (data) => {
        if (data.error) {
          console.error("api error:", data);
          throw Error(data);
        }
        return data;
      },
    }),

  useCreateBehaviourType: (callback) =>
    useMutation({
      mutationFn: (data) => adminEndpoints.createBehaviourType(data),
      onSuccess: (data) => {
        queryClient.invalidateQueries({ queryKey: ["ADMIN_BEHAVIOUR_TYPES"] });
        if (data.error) throw new Error(data.message);
        callback && callback(data);
      },
      onError: (error) => console.error("Error creating behaviour type", error),
    }),

  useUpdateBehaviourType: (callback) =>
    useMutation({
      mutationFn: (data) => adminEndpoints.updateBehaviourType(data),
      onSuccess: (data) => {
        queryClient.invalidateQueries({ queryKey: ["ADMIN_BEHAVIOUR_TYPES"] });
        if (data.error) throw new Error(data.message);
        callback && callback(data);
      },
      onError: (error) => console.error("Error updating behaviour type", error),
    }),

  useDeleteBehaviourType: (callback) =>
    useMutation({
      mutationFn: (id) => adminEndpoints.deleteBehaviourType(id),
      onSuccess: (data) => {
        queryClient.invalidateQueries({ queryKey: ["ADMIN_BEHAVIOUR_TYPES"] });
        if (data.error) throw new Error(data.message);
        callback && callback(data);
      },
      onError: (error) => console.error("Error deleting behaviour type", error),
    }),

  useCreateBehaviourGroup: (callback) =>
    useMutation({
      mutationFn: (data) => adminEndpoints.createBehaviourGroup(data),
      onSuccess: (data) => {
        queryClient.invalidateQueries({ queryKey: ["ADMIN_BEHAVIOUR_GROUPS"] });
        if (data.error) throw new Error(data.message);
        callback && callback(data);
      },
      onError: (error) =>
        console.error("Error creating behaviour group", error),
    }),

  useUpdateBehaviourGroup: (callback) =>
    useMutation({
      mutationFn: (data) => adminEndpoints.updateBehaviourGroup(data),
      onSuccess: (data) => {
        queryClient.invalidateQueries({ queryKey: ["ADMIN_BEHAVIOUR_GROUPS"] });
        if (data.error) throw new Error(data.message);
        callback && callback(data);
      },
      onError: (error) =>
        console.error("Error updating behaviour group", error),
    }),

  useDeleteBehaviourGroup: (callback) =>
    useMutation({
      mutationFn: (id) => adminEndpoints.deleteBehaviourGroup(id),
      onSuccess: (data) => {
        queryClient.invalidateQueries({ queryKey: ["ADMIN_BEHAVIOUR_GROUPS"] });
        if (data.error) throw new Error(data.message);
        callback && callback(data);
      },
      onError: (error) =>
        console.error("Error deleting behaviour group", error),
    }),

  useCreateCustomerBehaviourGroup: (callback) =>
    useMutation({
      mutationFn: (data) => adminEndpoints.createCustomerBehaviourGroup(data),
      onSuccess: (data) => {
        queryClient.invalidateQueries({
          queryKey: ["ADMIN_CUSTOMER_BEHAVIOUR_GROUPS"],
        });
        if (data.error) throw new Error(data.message);
        callback && callback(data);
      },
      onError: (error) =>
        console.error("Error creating customer behaviour group", error),
    }),

  useUpdateCustomerBehaviourGroup: (callback) =>
    useMutation({
      mutationFn: (data) => adminEndpoints.updateCustomerBehaviourGroup(data),
      onSuccess: (data) => {
        queryClient.invalidateQueries({
          queryKey: ["ADMIN_CUSTOMER_BEHAVIOUR_GROUPS"],
        });
        if (data.error) throw new Error(data.message);
        callback && callback(data);
      },
      onError: (error) =>
        console.error("Error updating customer behaviour group", error),
    }),

  useDeleteCustomerBehaviourGroup: (callback) =>
    useMutation({
      mutationFn: (id) => adminEndpoints.deleteCustomerBehaviourGroup(id),
      onSuccess: (data) => {
        queryClient.invalidateQueries({
          queryKey: ["ADMIN_CUSTOMER_BEHAVIOUR_GROUPS"],
        });
        if (data.error) throw new Error(data.message);
        callback && callback(data);
      },
      onError: (error) =>
        console.error("Error deleting customer behaviour group", error),
    }),

  useTriggerReportsService: (callback) =>
    useMutation({
      mutationFn: (body) => adminEndpoints.triggerReportsService(body),
      onSuccess: (data) => {
        if (data.error) throw new Error(data.message);
        callback && callback(data);
      },
      onError: (error) => {
        console.error("Error triggering Reports Service", error);
      },
    }),

  // Uploads one of a customer's email logos: fetch a presigned PUT url from the
  // backend, then PUT the raw file straight to S3 (skipped in mock/demo mode).
  useUploadCustomerLogo: (callback) =>
    useMutation({
      mutationFn: async ({ customerCode, logoType, file }) => {
        const { uploadUrl } = await adminEndpoints.getCustomerLogoUploadUrl(
          customerCode,
          logoType,
        );
        if (AppUtils.isMockedApp) return { mocked: true };
        return adminService.uploadFileToPresignedUrl(uploadUrl, file);
      },
      onSuccess: (data, variables) => callback && callback(variables),
      onError: (error) => console.error("Error uploading customer logo", error),
    }),

  // to invalidate cache, please see more: https://tanstack.com/query/v4/docs/framework/react/guides/invalidations-from-mutations
};

export default AdminHooks;
